"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { api } from "@/lib/api";
import { onPresenceEvent } from "@/hooks/use-presence-connection";
import { useTabBadge } from "@/hooks/tab-badge-context";
import type { AppNotification } from "@/lib/notification-types";

/**
 * Feed de notificacoes do usuario, app-wide.
 *
 * UM `GET /notifications` no boot; dali em diante o estado vive de eventos
 * socket aplicados IN-PLACE (regra de performance #1 do Chat — zero refetch por
 * evento). O transporte e a sala `user:{id}` do namespace `/chat`, que a
 * conexao presence-only do layout ja mantem aberta: nenhuma conexao nova.
 *
 * Um listener + um reducer por evento, sem `onAny` nem fallback generico —
 * padrao canonico do projeto: mais verboso, mas cada lacuna vira erro de tipo e
 * teste isolado.
 */
interface State {
  items: AppNotification[];
  unreadCount: number;
  /** false quando o servidor devolveu menos itens do que o pedido — fim da lista. */
  temMais: boolean;
  carregandoMais: boolean;
}

type Action =
  | { type: "BOOT"; items: AppNotification[]; unreadCount: number }
  | { type: "APPEND"; items: AppNotification[]; acabou: boolean }
  | { type: "CREATED"; item: AppNotification }
  | { type: "READ"; id: string }
  | { type: "READ_ALL" }
  | { type: "DELETED"; id: string };

/** Itens por página. Mesmo valor no boot e no "carregar mais". */
const PAGINA = 20;

const inicial: State = {
  items: [],
  unreadCount: 0,
  temMais: false,
  carregandoMais: false,
};

export function notificationsReducer(state: State, action: Action): State {
  switch (action.type) {
    case "BOOT":
      return {
        ...state,
        items: action.items,
        unreadCount: action.unreadCount,
        // Página cheia sugere que há mais além dela.
        temMais: action.items.length >= PAGINA,
        carregandoMais: false,
      };

    case "APPEND": {
      // Dedup por id: uma notificação nova chegando por socket entre o clique e
      // a resposta desloca a janela do cursor e traria repetida.
      const conhecidos = new Set(state.items.map((i) => i.id));
      const novos = action.items.filter((i) => !conhecidos.has(i.id));
      return {
        ...state,
        items: [...state.items, ...novos],
        temMais: !action.acabou,
        carregandoMais: false,
      };
    }

    case "CREATED": {
      // Dedup por id: o mesmo evento pode chegar duas vezes (reconexao,
      // re-broadcast). Sem isso o sino mostra a notificacao duplicada.
      if (state.items.some((i) => i.id === action.item.id)) return state;
      return {
        ...state,
        items: [action.item, ...state.items],
        unreadCount: state.unreadCount + (action.item.readAt ? 0 : 1),
      };
    }

    case "READ": {
      const alvo = state.items.find((i) => i.id === action.id);
      // Ja lida (ou desconhecida): nao mexe no contador, senao ele fica negativo
      // quando duas abas marcam a mesma notificacao.
      if (!alvo || alvo.readAt) return state;
      return {
        ...state,
        items: state.items.map((i) =>
          i.id === action.id ? { ...i, readAt: new Date().toISOString() } : i,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }

    case "READ_ALL":
      return {
        ...state,
        items: state.items.map((i) =>
          i.readAt ? i : { ...i, readAt: new Date().toISOString() },
        ),
        unreadCount: 0,
      };

    case "DELETED": {
      if (action.id === "all") return inicial;
      const alvo = state.items.find((i) => i.id === action.id);
      if (!alvo) return state;
      return {
        ...state,
        items: state.items.filter((i) => i.id !== action.id),
        // So decrementa se a apagada era NAO-LIDA.
        unreadCount: alvo.readAt
          ? state.unreadCount
          : Math.max(0, state.unreadCount - 1),
      };
    }

    default:
      return state;
  }
}

interface NotificationsValue extends State {
  carregarMais: () => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  removeAll: () => void;
}

const NotificationsContext = createContext<NotificationsValue>({
  ...inicial,
  carregarMais: () => {},
  markRead: () => {},
  markAllRead: () => {},
  remove: () => {},
  removeAll: () => {},
});

export function useNotifications(): NotificationsValue {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(notificationsReducer, inicial);
  // Fora do TabBadgeProvider isto e um no-op — o provider e opcional.
  const { setPersistentCount } = useTabBadge();

  // Espelha o contador do sino na aba do navegador.
  useEffect(() => {
    setPersistentCount(state.unreadCount);
  }, [state.unreadCount, setPersistentCount]);

  // Boot: unico GET da feature.
  useEffect(() => {
    let vivo = true;
    api
      .request<{ items: AppNotification[]; unreadCount: number }>(
        `/notifications?limit=${PAGINA}`,
      )
      .then((d) => {
        if (!vivo) return;
        dispatch({
          type: "BOOT",
          items: d?.items ?? [],
          unreadCount: d?.unreadCount ?? 0,
        });
      })
      .catch(() => {
        // Sem permissao / offline: fica vazio em vez de badge fantasma.
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Um listener por evento — nada de onAny.
  useEffect(() => {
    const offs = [
      onPresenceEvent("notification:created", (d) =>
        dispatch({ type: "CREATED", item: d as AppNotification }),
      ),
      onPresenceEvent("notification:read", (d) =>
        dispatch({ type: "READ", id: (d as { id: string }).id }),
      ),
      onPresenceEvent("notification:read-all", () => dispatch({ type: "READ_ALL" })),
      onPresenceEvent("notification:deleted", (d) =>
        dispatch({ type: "DELETED", id: (d as { id: string }).id }),
      ),
    ];
    return () => offs.forEach((off) => off());
  }, []);

  // Acoes: aplicam OTIMISTA e mandam o HTTP. O servidor re-emite o evento pras
  // outras abas; nesta, o reducer ja aplicou e o eco vira no-op idempotente.
  const markRead = useCallback((id: string) => {
    dispatch({ type: "READ", id });
    api.request(`/notifications/${id}/read`, { method: "PATCH" }).catch(() => {});
  }, []);

  const markAllRead = useCallback(() => {
    dispatch({ type: "READ_ALL" });
    api.request("/notifications/read-all", { method: "PATCH" }).catch(() => {});
  }, []);

  const remove = useCallback((id: string) => {
    dispatch({ type: "DELETED", id });
    api.request(`/notifications/${id}`, { method: "DELETE" }).catch(() => {});
  }, []);

  /**
   * Carrega a próxima página, ancorada no ÚLTIMO item da lista. Cursor em vez
   * de offset porque a lista muda embaixo do usuário: notificação nova entra no
   * topo o tempo todo, e offset traria itens repetidos ou pulados.
   */
  const carregarMais = useCallback(() => {
    const ultimo = state.items[state.items.length - 1];
    if (!ultimo || state.carregandoMais || !state.temMais) return;

    dispatch({ type: "APPEND", items: [], acabou: false });
    api
      .request<{ items: AppNotification[] }>(
        `/notifications?limit=${PAGINA}&before=${ultimo.id}`,
      )
      .then((d) => {
        const items = d?.items ?? [];
        dispatch({ type: "APPEND", items, acabou: items.length < PAGINA });
      })
      .catch(() => {
        dispatch({ type: "APPEND", items: [], acabou: true });
      });
  }, [state.items, state.carregandoMais, state.temMais]);

  const removeAll = useCallback(() => {
    dispatch({ type: "DELETED", id: "all" });
    api.request("/notifications", { method: "DELETE" }).catch(() => {});
  }, []);

  // Value memoizado: sem isso, todo render do provider cria objeto novo e
  // re-renderiza TODOS os consumidores, mesmo quando nada mudou. Os `children`
  // vem como prop do layout (referencia estavel), entao a arvore do dashboard
  // NAO re-renderiza — so quem chama useNotifications().
  const value = useMemo(
    () => ({ ...state, carregarMais, markRead, markAllRead, remove, removeAll }),
    [state, carregarMais, markRead, markAllRead, remove, removeAll],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
